import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import apiClient, { applicationsAPI } from '@/utils/api';
import { DocumentFileLink } from '@/components/candidate/DocumentFileLink';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  ArrowLeft, Mail, Phone, MapPin,
  CheckCircle2, XCircle, Clock, User, Briefcase, Eye, MessageCircle, CalendarClock
} from 'lucide-react';
import { ProposeInterviewModal } from '@/components/interviews/ProposeInterviewModal';
import { useApplicationFocus } from '@/hooks/useApplicationFocus';

const API = process.env.REACT_APP_BACKEND_URL;

// Files require a Bearer token (this app has no auth cookies across origins) —
// a plain <a>/window.open to the raw URL sends no Authorization header and
// silently 401s/403s. Fetch as an authenticated blob instead (goes through the
// shared axios instance, which auto-refreshes an expired access token once).

// Cape Verde numbers are 7 digits (+238). Keep any existing country code.
const waNumber = (phone) => {
  const digits = String(phone || '').replace(/\D/g, '');
  return digits.length === 7 ? `238${digits}` : digits;
};

const STATUS = {
  pending:  { i18nKey: 'pending',  color: 'text-amber-800 bg-amber-50 border-amber-200',       stripe: 'bg-amber-400',   Icon: Clock },
  reviewed: { i18nKey: 'reviewed', color: 'text-blue-800 bg-blue-50 border-blue-200',          stripe: 'bg-blue-500',    Icon: Eye },
  accepted: { i18nKey: 'accepted', color: 'text-emerald-800 bg-emerald-50 border-emerald-200', stripe: 'bg-emerald-500', Icon: CheckCircle2 },
  rejected: { i18nKey: 'rejected', color: 'text-red-800 bg-red-50 border-red-200',             stripe: 'bg-red-400',     Icon: XCircle }
};

const timeAgo = (iso, t) => {
  if (!iso) return '—';
  try {
    const diffMs = Date.now() - new Date(iso).getTime();
    const m = Math.floor(diffMs / 60000);
    if (m < 1) return t('employerApplications.justNow');
    if (m < 60) return t('employerApplications.minAgo', { count: m });
    const h = Math.floor(m / 60);
    if (h < 24) return t('employerApplications.hoursAgo', { count: h });
    const d = Math.floor(h / 24);
    if (d < 30) return t('employerApplications.daysAgo', { count: d });
    return new Date(iso).toLocaleDateString('pt-PT');
  } catch { return '—'; }
};

export default function EmployerApplications() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const jobIdFilter = params.get('job');
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [feedback, setFeedback] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [proposeFor, setProposeFor] = useState(null);
  const focus = useApplicationFocus(items, loading, setStatusFilter);

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [jobIdFilter, focus.navigationKey]);

  const load = async () => {
    setLoading(true);
    try {
      const url = jobIdFilter
        ? `/applications/job/${jobIdFilter}`
        : `/applications/employer/all`;
      const { data } = await apiClient.get(url);
      setItems(data);
    } catch (e) {
      setFeedback(e?.response?.data?.detail || t('employerApplications.errorLoad'));
    } finally {
      setLoading(false);
    }
  };

  // Employer opened the CV / contacted the candidate → mark as viewed (pending → reviewed) and notify candidate once
  const markViewed = async (id) => {
    const app = items.find((it) => it.id === id);
    if (!app || app.viewed_at) return;
    try {
      const { data } = await applicationsAPI.markViewed(id);
      setItems((prev) => prev.map((it) => (it.id === id ? { ...it, status: data.status || it.status, viewed_at: data.viewed_at } : it)));
    } catch { /* silent */ }
  };

  const updateStatus = async (id, newStatus) => {
    try {
      await applicationsAPI.updateStatus(id, newStatus);
      setItems(items.map(it => it.id === id ? { ...it, status: newStatus } : it));
    } catch (e) {
      alert(e?.response?.data?.detail || t('employerApplications.errorUpdate'));
    }
  };

  const counts = useMemo(() => {
    const c = { all: items.length, pending: 0, reviewed: 0, accepted: 0, rejected: 0 };
    items.forEach(i => { if (c[i.status] !== undefined) c[i.status]++; });
    return c;
  }, [items]);

  const filtered = useMemo(() => (
    statusFilter === 'all' ? items : items.filter(i => i.status === statusFilter)
  ), [items, statusFilter]);

  const needsAction = counts.pending;

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-5xl mx-auto px-4 sm:px-6">
        <Button variant="ghost" onClick={() => navigate('/dashboard')} className="mb-4 -ml-2 text-gray-600" data-testid="back-to-dashboard">
          <ArrowLeft className="h-4 w-4 mr-2" />{t('employerApplications.backToDashboard')}
        </Button>

        {/* Header */}
        <div className="flex items-start justify-between flex-wrap gap-4 mb-5">
          <div>
            <h1 className="text-3xl sm:text-4xl font-bold tracking-tight text-gray-900">{t('employerApplications.title')}</h1>
            <p className="text-gray-600 mt-1 text-sm sm:text-base">
              {jobIdFilter ? t('employerApplications.subtitleForJob') : t('employerApplications.subtitleAll')}
              {needsAction > 0 && (
                <span className="ml-2 inline-flex items-center gap-1.5 text-amber-800 font-medium">
                  <span className="w-1.5 h-1.5 rounded-full bg-amber-500 animate-pulse"></span>
                  {t('employerApplications.needsResponse', { count: needsAction })}
                </span>
              )}
            </p>
          </div>
          {jobIdFilter && (
            <Button variant="outline" size="sm" onClick={() => navigate('/applications')}>
              {t('employerApplications.viewAllApplications')}
            </Button>
          )}
        </div>

        {/* Filter tabs */}
        <Card className="p-2 mb-5 border-gray-200" data-testid="applications-filter-bar">
          <div className="flex items-center gap-1 overflow-x-auto scrollbar-thin">
            {[
              { key: 'all', i18nKey: 'all', count: counts.all },
              { key: 'pending', i18nKey: 'pending', count: counts.pending, urgent: true },
              { key: 'reviewed', i18nKey: 'reviewed', count: counts.reviewed },
              { key: 'accepted', i18nKey: 'accepted', count: counts.accepted },
              { key: 'rejected', i18nKey: 'rejected', count: counts.rejected }
            ].map(f => {
              const active = statusFilter === f.key;
              return (
                <button
                  key={f.key}
                  onClick={() => setStatusFilter(f.key)}
                  className={`flex-shrink-0 px-3 py-2 rounded-md text-sm font-medium transition-all flex items-center gap-2 ${
                    active ? 'bg-gray-900 text-white shadow-sm' : 'text-gray-600 hover:bg-gray-100'
                  }`}
                  data-testid={`filter-${f.key}`}
                >
                  <span>{t(`employerApplications.status.${f.i18nKey}`)}</span>
                  <span className={`inline-flex items-center justify-center text-xs px-1.5 min-w-[20px] h-5 rounded-full font-semibold ${
                    active
                      ? 'bg-white/20 text-white'
                      : f.urgent && f.count > 0
                        ? 'bg-amber-100 text-amber-800'
                        : 'bg-gray-100 text-gray-700'
                  }`}>
                    {f.count}
                  </span>
                </button>
              );
            })}
          </div>
        </Card>

        {feedback && <Alert variant="destructive" className="mb-4"><AlertDescription>{feedback}</AlertDescription></Alert>}

        {focus.missing && <p role="status" className="mb-4 rounded-md bg-amber-50 p-3 text-sm text-amber-900" data-testid="notification-application-unavailable">{t('notifications.applicationUnavailable')}</p>}
        {loading ? (
          <div className="text-center py-12 text-gray-500">{t('employerApplications.loading')}</div>
        ) : filtered.length === 0 ? (
          <Card className="p-12 text-center border-dashed">
            <Briefcase className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-xl font-semibold mb-2">
              {items.length === 0 ? t('employerApplications.emptyTitle') : t('employerApplications.emptyFilterTitle')}
            </h3>
            <p className="text-gray-600">
              {items.length === 0
                ? t('employerApplications.emptyBody')
                : t('employerApplications.emptyFilterBody')}
            </p>
          </Card>
        ) : (
          <div className="space-y-3" data-testid="employer-applications-list">
            {filtered.map(app => (
              <ApplicationCard key={app.id} app={app} focused={app.id === focus.targetId} onUpdate={updateStatus} onViewed={markViewed} onPropose={setProposeFor} companyName={user?.company_name} t={t} />
            ))}
          </div>
        )}
        {proposeFor && (
          <ProposeInterviewModal
            app={proposeFor}
            open
            onClose={() => setProposeFor(null)}
            onAcceptOnly={() => updateStatus(proposeFor.id, 'accepted')}
            onProposed={(iv) => setItems((prev) => prev.map((it) => (it.id === proposeFor.id ? { ...it, status: 'accepted', interview_id: iv.id, interview_status: iv.status } : it)))}
          />
        )}
      </div>
    </div>
  );
}

/* -------------------- Application card -------------------- */
function ApplicationCard({ app, focused, onUpdate, onViewed, onPropose, companyName, t }) {
  const cfg = STATUS[app.status] || STATUS.pending;
  const StatusIcon = cfg.Icon;
  const cand = app.candidate || {};
  const name = cand.full_name || app.candidate_name || t('employerApplications.candidateFallback');
  const email = cand.email || app.candidate_email;
  const needsAction = app.status === 'pending';

  const docs = [
    (cand.cv_url || app.cv_url) && { url: cand.cv_url || app.cv_url, label: 'CV', testid: 'view-cv' },
    (cand.cover_letter_url || app.cover_letter_url) && { url: cand.cover_letter_url || app.cover_letter_url, label: t('employerApplications.docLetter'), testid: 'view-cover-letter' },
    cand.id_card_url && { url: cand.id_card_url, label: t('employerApplications.docIdCard'), testid: 'view-id-card' },
    cand.recognition_certificate_url && { url: cand.recognition_certificate_url, label: t('employerApplications.docCriminalRecord'), testid: 'view-recognition' },
    cand.certificate_url && { url: cand.certificate_url, label: t('employerApplications.docCertificate'), testid: 'view-certificate' },
    ...[...new Set([...(cand.certificates_urls || []), ...(cand.certificate_urls || [])])].map((u, i) => ({ url: u, label: `${t('employerApplications.docCertificateShort')} ${i + 1}`, testid: `view-certificate-extra-${i}` })),
    ...(cand.other_documents_urls || []).map((u, i) => ({ url: u, label: t('candidateProfile.upload.numberedFile', { count: i + 1 }), testid: `view-other-document-${i}` }))
  ].filter(Boolean);

  return (
    <Card
      id={`application-${app.id}`} tabIndex={-1}
      className={`relative scroll-mt-24 overflow-hidden border-gray-200 hover:border-gray-300 hover:shadow-md transition-all ${focused ? 'ring-2 ring-blue-500' : needsAction ? 'ring-1 ring-amber-200' : ''}`}
      data-testid={`application-card-${app.id}`}
    >
      {/* Left status stripe */}
      <div className={`absolute left-0 top-0 bottom-0 w-1 ${cfg.stripe}`} />

      <div className="p-5 pl-6">
        {/* Header row */}
        <div className="flex items-start gap-3 mb-3">
          <div className="w-11 h-11 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 text-white flex items-center justify-center text-lg font-bold flex-shrink-0">
            {name.charAt(0).toUpperCase()}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
              <h3 className="text-lg font-bold text-gray-900 leading-tight truncate">{name}</h3>
              <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-semibold border ${cfg.color}`}>
                <StatusIcon className="h-3 w-3" />{t(`employerApplications.status.${cfg.i18nKey}`)}
              </span>
              {needsAction && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-semibold bg-amber-100 text-amber-900 border border-amber-300">
                  <span className="w-1.5 h-1.5 rounded-full bg-amber-500 animate-pulse"></span>
                  {t('employerApplications.needsResponseShort')}
                </span>
              )}
            </div>
            <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-sm text-gray-600 mt-1">
              {app.job_title && (
                <span className="inline-flex items-center gap-1">
                  <Briefcase className="h-3.5 w-3.5 text-gray-400" />
                  <Link to={`/jobs/${app.job_id}`} className="text-blue-600 hover:underline truncate">{app.job_title}</Link>
                </span>
              )}
              <span className="text-gray-300">·</span>
              <span className="text-gray-500 text-xs">{t('employerApplications.appliedAt', { time: timeAgo(app.applied_at, t) })}</span>
            </div>
          </div>
        </div>

        {/* Contact + Meta */}
        <div className="flex flex-wrap gap-x-4 gap-y-1.5 text-sm text-gray-700 mb-3 pl-14">
          {email && (
            <a href={`mailto:${email}`} className="inline-flex items-center gap-1.5 hover:text-blue-600 hover:underline">
              <Mail className="h-3.5 w-3.5 text-gray-400" />{email}
            </a>
          )}
          {cand.phone && (
            <a href={`tel:${cand.phone}`} className="inline-flex items-center gap-1.5 hover:text-blue-600 hover:underline">
              <Phone className="h-3.5 w-3.5 text-gray-400" />{cand.phone}
            </a>
          )}
          {cand.phone && (
            <a
              href={`https://wa.me/${waNumber(cand.phone)}?text=${encodeURIComponent(t('employerApplications.whatsappMsg', { name: (cand.full_name || '').split(' ')[0], job: app.job_title || '', company: companyName || 'Trabadju.cv' }))}`}
              target="_blank" rel="noopener noreferrer"
              onClick={() => onViewed?.(app.id)}
              className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-800 px-2.5 py-0.5 font-semibold hover:bg-emerald-100"
              data-testid={`whatsapp-${app.id}`}
            >
              <MessageCircle className="h-3.5 w-3.5" />WhatsApp
            </a>
          )}
          {cand.location && (
            <span className="inline-flex items-center gap-1.5">
              <MapPin className="h-3.5 w-3.5 text-gray-400" />{cand.location}
            </span>
          )}
          {cand.experience_years != null && (
            <span className="inline-flex items-center gap-1.5">
              <Briefcase className="h-3.5 w-3.5 text-gray-400" />{t('employerApplications.yearsExp', { count: cand.experience_years })}
            </span>
          )}
        </div>

        {/* Summary / message */}
        {(cand.profile_summary || app.message) && (
          <div className="pl-14 space-y-2 mb-3">
            {cand.profile_summary && (
              <div className="text-sm text-gray-700 bg-gray-50 px-3 py-2 rounded-md border border-gray-100">
                <span className="font-semibold text-gray-900">{t('employerApplications.about')}:</span> {cand.profile_summary}
              </div>
            )}
            {app.message && (
              <div className="text-sm text-gray-700 bg-blue-50 px-3 py-2 rounded-md border border-blue-100 italic">
                &ldquo;{app.message}&rdquo;
              </div>
            )}
          </div>
        )}

        {/* Documents */}
        {docs.length > 0 && (
          <div className="pl-14 mb-3">
            <div className="flex flex-wrap gap-1.5">
              {docs.map((d, idx) => (
                <DocumentFileLink key={`${d.testid}-${idx}`} url={d.url} label={d.label}
                  onAccess={() => onViewed?.(app.id)} testId={`${d.testid}-${app.id}`} compact />
              ))}
            </div>
          </div>
        )}

        {/* Decision row */}
        <div className="flex flex-wrap items-center justify-end gap-2 pt-3 border-t border-gray-100">
          {app.interview_id && (
            <Link to="/interviews" className="mr-auto inline-flex items-center gap-1.5 text-xs font-semibold text-blue-700 bg-blue-50 border border-blue-200 rounded-md px-2 py-1 hover:bg-blue-100" data-testid={`interview-badge-${app.id}`}>
              <CalendarClock className="h-3.5 w-3.5" />{t('employerApplications.interviewInProgress')}
            </Link>
          )}
          {app.status === 'pending' && (
            <Button
              size="sm"
              variant="ghost"
              onClick={() => onUpdate(app.id, 'reviewed')}
              className="text-gray-700"
              data-testid={`mark-reviewed-${app.id}`}
            >
              <Eye className="h-4 w-4 mr-1.5" />{t('employerApplications.markReviewed')}
            </Button>
          )}
          {app.status !== 'rejected' && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => onUpdate(app.id, 'rejected')}
              className="border-red-200 text-red-700 hover:bg-red-50 hover:border-red-300"
              data-testid={`reject-${app.id}`}
            >
              <XCircle className="h-4 w-4 mr-1.5" />{t('employerApplications.reject')}
            </Button>
          )}
          {app.status !== 'accepted' && (
            <Button
              size="sm"
              onClick={() => onPropose(app)}
              className="bg-emerald-600 hover:bg-emerald-700 shadow-sm"
              data-testid={`accept-${app.id}`}
            >
              <CheckCircle2 className="h-4 w-4 mr-1.5" />{t('employerApplications.acceptInterview')}
            </Button>
          )}
          {app.status === 'accepted' && !app.interview_id && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => onPropose(app)}
              className="border-emerald-300 text-emerald-800 hover:bg-emerald-50"
              data-testid={`schedule-interview-${app.id}`}
            >
              <CalendarClock className="h-4 w-4 mr-1.5" />{t('employerApplications.scheduleInterview')}
            </Button>
          )}
        </div>
      </div>
    </Card>
  );
}
